/* eslint-disable react/prop-types */
import React, { useEffect, useState, useContext, memo } from 'react';
import PropTypes from 'prop-types';
import { Advert, destroySlot } from '@zillow/mst-display-ad';
import { useYieldCallback } from '@zillow/yield-callback';
import { Text } from '@zillow/constellation';
import { DQRulesContext } from '@zillow/hollywood-common';
import getUserCookiePreferences from '@zillow/onetrust-cookie-parser';
import CasaleScriptLoader from '../../util/casaleScriptLoader';

let buildTarget = null;
let casale = null;
function deviceType(props) {
    const { isMobile, isTablet } = props;
    let device = 'desktop';
    if (isTablet) {
        device = 'tablet';
        return device;
    }
    if (isMobile) {
        device = 'mobile';
        return device;
    }
    return device;
}

export const AD_TYPE = {
    GENERIC_CENTER_AD: 'GenericCenteredAd',
    GENERIC_WIDE_AD: 'GenericWideAd',
    SPONSORED_LINK_AD: 'SponsoredLinkAd',
    ACCIPITER_AD: 'AccipiterAd',
    FLEX_AD: 'FlexAd',
};

function sponsoredAd() {
    return (
        <div>
            <p className="advertisement">Sponsored Links</p>
            <Text className="sponsoredlinks ad generic-box" as="div" marginBottom="md">
                <div className="inner" />
            </Text>
        </div>
    );
}

const GetAd = memo(
    ({
        className,
        inventory,
        eventName,
        setIsAdResponseEmpty,
        variant,
        targetDiv,
        isPrebidAllowed,
    }) => {
        const adCallback = useYieldCallback(
            e => {
                setAdSlotLoadedStatus(e, eventName, inventory, setIsAdResponseEmpty);
            },
            [eventName, inventory, setIsAdResponseEmpty]
        );
        return (
            <Advert
                adInventory={inventory}
                className={className}
                targetDiv={targetDiv}
                isPrebidAllowed={isPrebidAllowed}
                prebidPageType={isPrebidAllowed ? 'zillow_hdp' : null}
                isSRA
                variant={variant}
                eventList={['slotRenderEnded']}
                onSlotRenderEnded={adCallback}
            />
        );
    }
);

function getAdType({
    adType,
    inventory,
    className,
    createDeferredTarget,
    deferUntilExplicitLoad,
    setIsAdResponseEmpty,
    eventName,
    targetDiv,
    variant,
    explicitLoadClass,
    isPrebidAllowed,
}) {
    if (createDeferredTarget) {
        buildTarget = (
            <GetAd
                className={`${className} deferred-iframe-target`}
                inventory={inventory}
                eventName={eventName}
                setIsAdResponseEmpty={setIsAdResponseEmpty}
                targetDiv={targetDiv}
                variant={variant}
                isPrebidAllowed={isPrebidAllowed}
            />
        );
    } else {
        const allowtransparency = 'true';
        buildTarget = (
            <GetAd
                className={`${className} generic-box ad third-party-ad wide third-party-iframe-ad-container`}
                inventory={inventory}
                eventName={eventName}
                setIsAdResponseEmpty={setIsAdResponseEmpty}
                targetDiv={targetDiv}
                variant={variant}
                isPrebidAllowed={isPrebidAllowed}
                allowtransparency={allowtransparency}
            />
        );
    }

    const deferrLoad = (
        <div>
            <GetAd
                className={`${className} deferred-iframe-target`}
                inventory={inventory}
                eventName={eventName}
                setIsAdResponseEmpty={setIsAdResponseEmpty}
                targetDiv={targetDiv}
                variant={variant}
                isPrebidAllowed={isPrebidAllowed}
            />

            <input
                value="false"
                type="hidden"
                id={`${targetDiv}-rendered`}
                className={explicitLoadClass}
                data-frame-id={targetDiv}
                data-url=""
            />
        </div>
    );

    const ad = {
        [AD_TYPE.GENERIC_CENTER_AD]: (
            <GetAd
                className={`${className} generic-box ad third-party-ad wide third-party-iframe-ad-container`}
                inventory={inventory}
                eventName={eventName}
                setIsAdResponseEmpty={setIsAdResponseEmpty}
                targetDiv={targetDiv}
                variant={variant}
                isPrebidAllowed={isPrebidAllowed}
            />
        ),
        [AD_TYPE.GENERIC_WIDE_AD]: (
            <GetAd
                className={`${className} generic-box ad third-party-ad wide third-party-iframe-ad-container`}
                inventory={inventory}
                eventName={eventName}
                setIsAdResponseEmpty={setIsAdResponseEmpty}
                targetDiv={targetDiv}
                variant={variant}
                isPrebidAllowed={isPrebidAllowed}
            />
        ),
        [AD_TYPE.SPONSORED_LINK_AD]: sponsoredAd(),
        [AD_TYPE.ACCIPITER_AD]: buildTarget,
        [AD_TYPE.FLEX_AD]: deferUntilExplicitLoad ? deferrLoad : buildTarget,
    };

    return ad[adType] || null;
}

function enableCasale() {
    // initialize Casale script loader singleton
    if (casale) {
        return;
    }
    casale = new CasaleScriptLoader();
}

function onEmptyResponse({ adUnitPath, inventory, setIsAdResponseEmpty }) {
    if (adUnitPath && adUnitPath.includes(inventory?.position)) {
        setIsAdResponseEmpty(true);
    }
}

function setAdSlotLoadedStatus(event = {}, eventName, inventory, setIsAdResponseEmpty) {
    if (event.isEmpty && typeof onEmptyResponse === 'function') {
        const adUnitPath = event.slot && event.slot.getAdUnitPath();
        onEmptyResponse({ adUnitPath, inventory, setIsAdResponseEmpty });
    }
}

const MstHomeDetailsGoogleAds = props => {
    const {
        targetDiv,
        eventName,
        variant,
        targets,
        deferred,
        versOn = '',
        fallback,
        isPrebidAllowed,
        adResponseCallback,
        isMobile,
        isIAB,
        guid,
    } = props;
    const device = deviceType(props);
    const context = useContext(DQRulesContext);
    const [isMounted, setIsMounted] = useState(false);
    const [isAdResponseEmpty, setIsAdResponseEmpty] = useState(false);
    const [inventory, setInventory] = useState(null);
    useEffect(() => {
        import('@zillow/mst-ad-slot-manager').then(module => {
            const { dataQualityRules } = context;
            const isAdsRestricted = dataQualityRules?.getRule('isAdsRestricted');
            let mergedTargets;
            if (targets) {
                const hdpVersValue = 'deferred';
                const mstVersValue =
                    versOn !== '' ? `${hdpVersValue},${versOn}` : `${hdpVersValue}`;
                mergedTargets = { ...targets, vers: mstVersValue, guid };
            }
            const inventoryList = variant
                ? module[variant].getInventory({
                      targetDiv,
                      device,
                      rules: {
                          pageType: mergedTargets?.listtp || 'not_for_sale',
                          targets: mergedTargets || {},
                          isRestricted: isAdsRestricted,
                      },
                  })
                : {};
            setInventory(inventoryList);
            setIsMounted(true);
        });
        return () => {
            destroySlot(targetDiv);
        };
    }, [context, variant, deferred, device, eventName, targetDiv, targets, versOn, guid]);

    useEffect(() => {
        const isAdvertisingCookieBlocked =
            getUserCookiePreferences() !== null &&
            getUserCookiePreferences().isAdvertisingCookieBlocked;

        if (!isAdvertisingCookieBlocked && isIAB) {
            enableCasale();
        }
    });

    useYieldCallback(() => {
        if (adResponseCallback) {
            adResponseCallback(isAdResponseEmpty);
        }
    }, [isAdResponseEmpty, adResponseCallback]);

    const {
        isAdsOn,
        adType,
        className,
        refreshOnUpdate,
        createDeferredTarget,
        explicitLoadClass,
        showNoData,
    } = props;

    if (isAdResponseEmpty && showNoData) {
        return <div>No Data</div>;
    }

    if (isAdResponseEmpty && fallback) {
        if (isMobile) {
            props.showActionBar();
        }

        return fallback;
    }

    if (isAdsOn === false || !isMounted) {
        return null;
    }
    if (inventory && isMounted) {
        const { pageType = '', position = '' } = inventory;
        const isMortgageAd = position.includes('mortgage');
        const isSold = pageType.includes('buy_sold');
        const isNotForSale = pageType.includes('not_for_sale');
        if (isMortgageAd && (isSold || isNotForSale)) {
            return null;
        }
        return getAdType({
            adType,
            inventory,
            targetDiv,
            className,
            variant,
            refreshOnUpdate,
            createDeferredTarget,
            eventName,
            isPrebidAllowed,
            adResponseCallback,
            setIsAdResponseEmpty,
            explicitLoadClass,
        });
    }
    return null;
};

MstHomeDetailsGoogleAds.propTypes = {
    adType: PropTypes.string,
    createDeferredTarget: PropTypes.bool,
    refreshOnUpdate: PropTypes.bool,
    className: PropTypes.string,
    showNoData: PropTypes.bool,
    explicitLoadClass: PropTypes.string,
    eventName: PropTypes.string,
    targetDiv: PropTypes.string,
    variant: PropTypes.string.isRequired,
    fallback: PropTypes.node,
    targets: PropTypes.object,
    guid: PropTypes.string,
    isAdsOn: PropTypes.bool,
    showActionBar: PropTypes.func,
    deferred: PropTypes.bool,
    versOn: PropTypes.string,
    isPrebidAllowed: PropTypes.bool,
    adResponseCallback: PropTypes.func,
    isMobile: PropTypes.bool,
    isIAB: PropTypes.bool,
};

MstHomeDetailsGoogleAds.defaultProps = {
    adType: AD_TYPE.GENERIC_WIDE_AD,
    createDeferredTarget: true,
    explicitLoadClass: 'explicitly-deferred-ad',
    className: '',
    refreshOnUpdate: false,
    showNoData: false,
};

export default MstHomeDetailsGoogleAds;

/** export default connect(state => ({
 *    isMobile: state.appState.isMobile_DeprecatedDoNotUse,
 *    isTablet: state.appState.isTablet_DeprecatedDoNotUse,
 *    isAdsOn: state.appState.isThirdPartyAdsOn,
 *    guid: state?.appState?.keystoneData?._guid,
 *    showActionBar,
 *}))(MstHomeDetailsGoogleAds);*/

MstHomeDetailsGoogleAds.sentryTeamOwner = 'media-solutions';
